import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Task } from "@common/types/task";
import { User } from "@common/types/user";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useUpdateTask } from "@api/task.api";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { memo, useCallback, useMemo } from "react";
import { HStack } from "@design-system/HStack/HStack";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { NestedTaskRow } from "../../types";
import { GroupAssigneesSummary } from "./TaskGroupSummaryCells";
import { TaskAssigneeCellPicker } from "./TaskAssigneeCellPicker";
import { getAssigneeHiddenTooltip } from "./assigneeHiddenTooltip";
import styles from "./TaskViewCells.module.scss";

const TaskAssigneeCell = memo(
    ({ row }: CellContext<ViewTableFeatures, NestedTaskRow, unknown>) => {
        const task = row.original as Task;
        const updateTask = useUpdateTask(task);
        const { data: members = [] } = useCurrentWorkspaceMembers();

        const assignee = useMemo(
            () =>
                task.assigneeId
                    ? (members.find((m) => m.user.id === task.assigneeId) ??
                      null)
                    : null,
            [task.assigneeId, members],
        );

        const hiddenTooltip = useMemo(
            () => (assignee ? getAssigneeHiddenTooltip(task, assignee) : null),
            [task, assignee],
        );

        const handleUpdate = useCallback(
            (newAssigneeId: User["id"] | null) => {
                updateTask.mutate({ assigneeId: newAssigneeId });
            },
            [updateTask],
        );

        const trigger = useMemo(() => {
            const content = assignee ? (
                <HStack
                    gap={8}
                    align="center"
                    className={
                        hiddenTooltip ? styles.assigneeHidden : undefined
                    }
                >
                    <UserAvatar user={assignee.user} size="sm" aria-hidden />
                    <BreakableText text={assignee.user.name} showTooltip />
                </HStack>
            ) : undefined;

            return (
                <Picker.CellContent
                    addText="Assign to"
                    hideAddIcon
                    buttonProps={{ tooltip: hiddenTooltip }}
                    value={task.assigneeId ? (content ?? "") : undefined}
                    aria-label={assignee?.user.name ?? undefined}
                />
            );
        }, [task.assigneeId, assignee, hiddenTooltip]);

        if (row.original.isNewPlaceholder) {
            return null;
        }

        return (
            <ViewTableCell>
                <TaskAssigneeCellPicker
                    value={task.assigneeId ?? null}
                    members={members}
                    onValueChange={handleUpdate}
                    trigger={trigger}
                />
            </ViewTableCell>
        );
    },
);

export const AssigneeCell = (
    props: CellContext<ViewTableFeatures, NestedTaskRow, unknown>,
) =>
    props.row.original.isGroup ? (
        <GroupAssigneesSummary {...props} />
    ) : (
        <TaskAssigneeCell {...props} />
    );
